import { DataNote, DataNoteId } from '@employ-engage/data';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { InfoNote } from './info-note';

/**
 * The small print under a chart or a table, plus the reasoning behind it.
 *
 * The short line stays on screen and the explanation goes behind the ⓘ. That
 * split is the point: a reader who never opens the note still knows a total is
 * withheld rather than reading a dash as nothing, and a reader who wonders why
 * gets an answer without having to ask anybody.
 */

export interface DataNotesProps {
  notes: DataNote[];
  /**
   * Stacked under a table, or run together beneath a chart where the vertical
   * room belongs to the plot.
   */
  layout?: 'block' | 'inline';
  /**
   * Short lines to show before the rest live only inside the note. Notes come
   * in reading order, so trimming from the end is safe.
   */
  maxLines?: number;
  /**
   * Which notes earn a short line, when reading order is not the right answer.
   * A chart keeps the two that say how to read its marks; the rest of the
   * reasoning is still in the note. Omit to take the first `maxLines`.
   */
  showShort?: DataNoteId[];
  className?: string;
}

export function DataNotes({
  notes,
  layout = 'block',
  maxLines = 2,
  showShort,
  className,
}: DataNotesProps) {
  const { t } = useTranslation(['common']);

  if (!notes.length) return null;

  const visible = showShort
    ? notes.filter((note) => showShort.includes(note.id))
    : notes.slice(0, maxLines);
  const inline = layout === 'inline';

  // Every note's reasoning, including the ones whose short line was trimmed.
  const explanation = (
    <InfoNote
      label={t('Why these numbers look like this')}
      className="ml-1 align-middle"
    >
      <p className="mb-2 font-semibold">{t('About these numbers')}</p>
      <ul className="space-y-2">
        {notes.map((note) => (
          <li key={note.id}>{t(note.long, note.values)}</li>
        ))}
      </ul>
    </InfoNote>
  );

  return (
    <div
      className={classNames(
        'text-xs text-light-secondaryText dark:text-dark-secondaryText',
        inline ? 'flex flex-wrap items-center gap-x-2' : 'space-y-1',
        className
      )}
    >
      {visible.map((note, i) => {
        const text = t(note.short, note.values);
        // The icon rides the last line rather than claiming one of its own,
        // which in a short tile is a line the chart needs.
        const last = i === visible.length - 1;
        return inline ? (
          <span key={note.id}>{text}</span>
        ) : (
          <p key={note.id}>
            {text}
            {last && explanation}
          </p>
        );
      })}
      {/* Notes can all be explanation and no short line — a chart whose only
          caveat is a withheld total says nothing under the plot, but the
          reasoning still has to be reachable. */}
      {(inline || !visible.length) && explanation}
    </div>
  );
}

export default DataNotes;
